import { useEffect, useMemo, useState } from "react";

import { getSortableName } from "utils/directory/sorting";
import { countCommunityRunsToGenerate } from "utils/communities/communityRunCount";

/**
 * Shared run planning stats for header UsageBar and Configuration dock.
 */
export const useCommunityRunStats = ({
    communityId,
    fetchCommunityRunHistory,
    readyModels = [],
    upgradePackages = [],
    ecmInputMap = {},
    modelUpgradesRun = {},
    runHistory = {},
}) => {
    const [runInfo, setRunInfo] = useState({});

    const runsUsedTotal = useMemo(
        () => Object.values(runHistory).reduce((sum, { size = 0 }) => sum + size, 0),
        [runHistory]
    );

    const numUpgradePackages = upgradePackages.length;

    const numToBeRun = useMemo(
        () =>
            countCommunityRunsToGenerate({
                ecmInputMap,
                upgradePackages,
                models: Object.fromEntries(readyModels),
                modelUpgradesRun,
            }),
        [ecmInputMap, upgradePackages, readyModels, modelUpgradesRun]
    );

    let numBaseFiles = 0;
    readyModels.forEach(([, modelObj]) => {
        numBaseFiles += modelObj.countDirection?.duplicates || 0;
    });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!communityId || !fetchCommunityRunHistory) {
                return;
            }

            const historyPayload = await fetchCommunityRunHistory(communityId);
            if (cancelled) {
                return;
            }

            const readyModelIds = readyModels.map(([id]) => id);
            let numPassedResults = 0;
            let numFailedResults = 0;
            let failedModelInfo = {};

            Object.values(historyPayload?.runResults || {}).forEach(({ results = {} }) => {
                Object.entries(results).forEach(([key, value]) => {
                    const modelId = key.split("_")[0];
                    if (!readyModelIds.includes(modelId)) {
                        return;
                    }

                    if (value.result === "PASS") {
                        numPassedResults += 1;
                    } else if (value.result === "FAIL") {
                        numFailedResults += 1;
                        const modelName = readyModels.find(([id]) => id === modelId)?.[1]?.name;
                        const failedPackageName = upgradePackages.reduce(
                            (acc, upgrade) => (upgrade.id === value.upgradePackageId ? upgrade.name : acc),
                            "Base Case"
                        );
                        failedModelInfo[modelId] = {
                            modelName,
                            upgradePackages: [
                                ...(failedModelInfo[modelId]?.upgradePackages || []),
                                failedPackageName,
                            ],
                        };
                    }
                });
            });

            const sortedFailedModelInfo = Object.entries(failedModelInfo).sort(([_, valueA], [__, valueB]) =>
                getSortableName(valueA?.modelName).localeCompare(getSortableName(valueB?.modelName))
            );

            setRunInfo({
                numFailed: numFailedResults,
                numCompleted: numPassedResults + numFailedResults,
                failedModelInfo: Object.fromEntries(sortedFailedModelInfo),
            });
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [
        communityId,
        fetchCommunityRunHistory,
        readyModels,
        numBaseFiles,
        numUpgradePackages,
        upgradePackages,
    ]);

    return {
        runInfo: {
            ...runInfo,
            numToBeRun,
            totalSimulations: runsUsedTotal + numToBeRun,
        },
        runsUsedTotal,
        numBaseFiles,
        numUpgradePackages,
    };
};
